import React, { useState, useCallback } from 'react';
import {
    List, Datagrid, TextField, EmailField, ReferenceField, DateField,
    Edit, SimpleForm, TextInput, ReferenceInput, SelectInput, DateInput,
    Create, ReferenceArrayInput, SelectArrayInput, TabbedForm, FormTab,
    required, minLength, useRecordContext, TopToolbar, CreateButton, ExportButton, FilterButton, ListButton,
    useNotify, useRefresh, usePermissions, SaveButton, Toolbar,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { jwtDecode } from 'jwt-decode';
import { roleOptionText } from '../utils/roleOptionText';
import { extractApiErrorMessage } from '../dataProvider';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Alert, LinearProgress, Chip, Divider,
    FormControlLabel, Checkbox, List as MuiList, ListItem,
    ListItemIcon, ListItemText, Paper,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const UserStatusChip = () => {
    const record = useRecordContext();
    if (!record) return null;
    const status = record.status || 'active';
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
        active: { label: 'Aktiv', color: 'success' },
        archived: { label: 'Archiviert', color: 'warning' },
        deleted: { label: 'Gelöscht', color: 'error' },
    };
    const { label, color } = map[status] || { label: status, color: 'default' as const };
    return <Chip label={label} color={color} size="small" />;
};
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

// ─── Import Dialog ────────────────────────────────────────────────────────────

interface ImportResult {
    success: boolean;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
    message: string;
}

type ImportBatchResponse = Partial<ImportResult> & { total?: number; error?: string };

const IMPORT_BATCH_SIZE = 25;

export const UserImportDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const [file, setFile] = useState<File | null>(null);
    const [updateExisting, setUpdateExisting] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const notify = useNotify();
    const refresh = useRefresh();

    const reset = () => {
        setFile(null);
        setResult(null);
        setProgress(null);
        setLoading(false);
        setUpdateExisting(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped && (dropped.name.endsWith('.csv') || dropped.type === 'text/csv')) {
            setFile(dropped);
            setResult(null);
        } else {
            notify('Bitte eine CSV-Datei auswählen', { type: 'warning' });
        }
    }, [notify]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0] ?? null;
        setFile(selected);
        setResult(null);
    };

    const handleImport = async () => {
        if (!file) return;
        setLoading(true);
        setResult(null);
        setProgress(null);

        const errors: string[] = [];
        let created = 0;
        let updated = 0;
        let skipped = 0;
        let done = 0;
        let total = 0;
        let failure: string | null = null;

        try {
            const token = localStorage.getItem('token');
            do {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('updateExisting', updateExisting ? 'true' : 'false');
                formData.append('offset', String(done));
                formData.append('limit', String(IMPORT_BATCH_SIZE));

                const response = await fetch(`${SSO_URL}/api/users/import`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                });

                let data: ImportBatchResponse;
                try {
                    data = await response.json();
                } catch {
                    failure = 'Zeitüberschreitung oder Serverfehler beim Import';
                    break;
                }

                if (!response.ok) {
                    failure = data.error || 'Import fehlgeschlagen';
                    break;
                }

                total = data.total ?? 0;
                created += data.created ?? 0;
                updated += data.updated ?? 0;
                skipped += data.skipped ?? 0;
                errors.push(...(data.errors ?? []));
                done = Math.min(done + IMPORT_BATCH_SIZE, total);
                if (done < total) setProgress({ done, total });
            } while (done < total);
        } catch {
            failure = 'Netzwerkfehler beim Import';
        }

        if (failure !== null) {
            notify(failure, { type: 'error' });
        }
        if (failure === null || done > 0) {
            setResult({
                success: failure === null,
                created,
                updated,
                skipped,
                errors: failure === null
                    ? errors
                    : [...errors, `Import abgebrochen nach ${done} von ${total} Zeilen: ${failure}`],
                message: `${created} erstellt, ${updated} aktualisiert, ${skipped} übersprungen`,
            });
        }
        if (created + updated > 0) {
            refresh();
        }
        setLoading(false);
    };

    const handleDownloadTemplate = () => {
        const token = localStorage.getItem('token');
        fetch(`${SSO_URL}/api/users/import/template`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'benutzer-import-vorlage.csv';
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => notify('Vorlage konnte nicht heruntergeladen werden', { type: 'error' }));
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadFileIcon />
                Benutzer importieren (CSV)
            </DialogTitle>

            <DialogContent dividers>
                {/* Info box */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Pflichtfeld:</strong> <code>email</code><br />
                    <strong>Optional:</strong> firstName, lastName, phone, mobile, street, zip, city, country, dateOfBirth, jobTitle, language, password, roles<br />
                    <strong>Datum:</strong> TT.MM.JJJJ oder JJJJ-MM-TT<br />
                    <strong>Rollen:</strong> z. B. <code>ROLE_USER</code> oder <code>ROLE_CONTENT_MANAGER</code> (mehrere mit Komma trennen, ohne Angabe ROLE_USER)<br />
                    Excel-Dateien (CSV, auch mit Semikolon und Umlauten) werden direkt erkannt.
                </Alert>

                {/* Download template */}
                <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                >
                    CSV-Vorlage herunterladen
                </Button>

                <Divider sx={{ mb: 2 }} />

                {/* Drop zone */}
                <Paper
                    variant="outlined"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    sx={{
                        border: dragging ? '2px dashed #1976d2' : '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: dragging ? '#e3f2fd' : 'transparent',
                        transition: 'all 0.2s',
                        mb: 2,
                    }}
                    onClick={() => document.getElementById('csv-file-input')?.click()}
                >
                    <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                        CSV hier ablegen oder <strong>klicken</strong> zum Auswählen
                    </Typography>
                    {file && (
                        <Chip label={file.name} color="primary" size="small" sx={{ mt: 1 }} />
                    )}
                    <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </Paper>

                {/* Options */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={updateExisting}
                            onChange={(e) => setUpdateExisting(e.target.checked)}
                        />
                    }
                    label="Bestehende Benutzer aktualisieren wenn E-Mail bereits existiert"
                />

                {/* Progress */}
                {loading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress
                            variant={progress ? 'determinate' : 'indeterminate'}
                            value={progress ? (progress.done / progress.total) * 100 : undefined}
                        />
                        {progress && (
                            <Typography variant="caption" color="text.secondary">
                                {`${progress.done} von ${progress.total} Zeilen verarbeitet`}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Result */}
                {result && (
                    <Box sx={{ mt: 2 }}>
                        <Alert
                            severity={result.errors.length > 0 ? 'warning' : 'success'}
                            sx={{ mb: 1 }}
                        >
                            {result.message}
                        </Alert>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            {result.created > 0 && (
                                <Chip icon={<CheckCircleIcon />} label={`${result.created} erstellt`} color="success" size="small" />
                            )}
                            {result.updated > 0 && (
                                <Chip icon={<CheckCircleIcon />} label={`${result.updated} aktualisiert`} color="info" size="small" />
                            )}
                            {result.skipped > 0 && (
                                <Chip icon={<WarningIcon />} label={`${result.skipped} übersprungen`} color="warning" size="small" />
                            )}
                        </Box>

                        {result.errors.length > 0 && (
                            <MuiList dense sx={{ maxHeight: 180, overflow: 'auto', bgcolor: '#fff8e1', borderRadius: 1 }}>
                                {result.errors.map((err, i) => (
                                    <ListItem key={i} disablePadding sx={{ px: 1 }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <ErrorIcon color="warning" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={err} primaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                ))}
                            </MuiList>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Schließen</Button>
                <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={handleImport}
                    disabled={!file || loading}
                >
                    Importieren
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── User List with Import button ─────────────────────────────────────────────

const UserListActions: React.FC = () => {
    const [importOpen, setImportOpen] = useState(false);
    return (
        <>
            <TopToolbar>
                <FilterButton />
                <CreateButton />
                <ExportButton />
                <Button
                    startIcon={<UploadFileIcon />}
                    onClick={() => setImportOpen(true)}
                    size="small"
                >
                    CSV Import
                </Button>
            </TopToolbar>
            <UserImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
        </>
    );
};

const UserTitle = () => {
    const record = useRecordContext();
    return <span>{record ? `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.email : ''}</span>;
};

const userFilters = [
    <TextInput label="Email" source="email" alwaysOn />,
    <TextInput label="Vorname" source="firstName" />,
    <TextInput label="Nachname" source="lastName" />,
    <TextInput label="Position" source="jobTitle" />,
    <ReferenceInput source="company.id" reference="companies" label="Firma" sort={{ field: 'name', order: 'ASC' }}>
        <SelectInput optionText="displayName" label="Firma" />
    </ReferenceInput>,
];

export const UserList = () => (
    <List filters={userFilters} actions={<UserListActions />}>
        <Datagrid rowClick="edit">
            <TextField source="firstName" label="Vorname" />
            <TextField source="lastName" label="Nachname" />
            <EmailField source="email" />
            <TextField source="jobTitle" />
            <UserStatusChip />
            <ReferenceField source="company" reference="companies" link="show" sortBy="company.name">
                <TextField source="displayName" />
            </ReferenceField>
        </Datagrid>
    </List>
);

const readApiError = async (response: Response, fallback: string): Promise<string> => {
    try {
        return extractApiErrorMessage(await response.json()) || fallback;
    } catch {
        return fallback;
    }
};

export const UserEditActions = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    const status = record?.status || 'active';

    // #362: Delete/Patch auf /api/users erlauben serverseitig nur Operator
    // und Company-Admin — anderen Rollen die Buttons gar nicht erst zeigen.
    const perms: string[] = Array.isArray(permissions) ? permissions : [];
    const canManageStatus = perms.includes('ROLE_OPERATOR') || perms.includes('ROLE_COMPANY_ADMIN');

    const handleStatusChange = async (newStatus: string) => {
        if (newStatus === 'deleted') {
            if (!window.confirm('Benutzer wirklich löschen? Die persönlichen Daten werden anonymisiert. Diese Aktion kann NICHT rückgängig gemacht werden.')) return;
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_SSO_URL}/api/users/${record?.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    notify(await readApiError(response, response.status === 403
                        ? 'Löschen nicht erlaubt — bitte an euren Unternehmensadmin wenden'
                        : 'Fehler beim Löschen'), { type: 'error' });
                    return;
                }
                notify('Benutzer wurde gelöscht und anonymisiert', { type: 'success' });
                refresh();
            } catch { notify('Fehler beim Löschen', { type: 'error' }); }
        } else {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_SSO_URL}/api/users/${record?.id}`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/merge-patch+json' },
                    body: JSON.stringify({ status: newStatus }),
                });
                if (!response.ok) {
                    notify(await readApiError(response, 'Fehler beim Statuswechsel'), { type: 'error' });
                    return;
                }
                notify(newStatus === 'archived' ? 'Benutzer archiviert' : 'Benutzer reaktiviert', { type: 'success' });
                refresh();
            } catch { notify('Fehler beim Statuswechsel', { type: 'error' }); }
        }
    };

    return (
        <TopToolbar>
            <ListButton />
            {canManageStatus && status === 'active' && (
                <Button startIcon={<ArchiveIcon />} onClick={() => handleStatusChange('archived')} color="warning" size="small">
                    Archivieren
                </Button>
            )}
            {canManageStatus && status === 'archived' && (
                <Button startIcon={<UnarchiveIcon />} onClick={() => handleStatusChange('active')} color="success" size="small">
                    Reaktivieren
                </Button>
            )}
            {canManageStatus && status !== 'deleted' && (
                <Button startIcon={<DeleteForeverIcon />} onClick={() => handleStatusChange('deleted')} color="error" size="small">
                    Löschen
                </Button>
            )}
        </TopToolbar>
    );
};

// E-Mail-Adressen sind der Login-Anker — Aenderungen sind Superadmin-Sache
// (azubi:web-Integration 2026-09). Serverseitig erzwingt das die
// EmailChangePolicy im SSO; hier wird das Feld passend gesperrt.
const mayChangeEmail = (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const decoded: any = jwtDecode(token);
        const roles: string[] = Array.isArray(decoded.roles)
            ? decoded.roles
            : Object.values(decoded.roles || {}) as string[];
        return ['ROLE_OPERATOR', 'ROLE_PLATFORM_ADMIN', 'ROLE_SUPER_ADMIN'].some(r => roles.includes(r));
    } catch {
        return false;
    }
};

export const UserEdit = () => {
    const notify = useNotify();
    const emailEditable = mayChangeEmail();
    return (
    <Edit title={<UserTitle />} actions={<UserEditActions />} mutationOptions={{ onSuccess: () => { notify('Benutzer erfolgreich aktualisiert', { type: 'success' }); } }} mutationMode="pessimistic">
        <TabbedForm>
            <FormTab label="resources.users.tabs.general">
                <TextInput source="id" disabled />
                <TextInput
                    source="email"
                    validate={required()}
                    type="email"
                    fullWidth
                    disabled={!emailEditable}
                    helperText={emailEditable ? undefined : 'resources.users.fields.emailLockedHelp'}
                />
                <TextInput source="firstName" fullWidth />
                <TextInput source="lastName" fullWidth />
                <TextInput
                    source="password"
                    type="password"
                    fullWidth
                    autoComplete="new-password"
                    validate={minLength(10)}
                    helperText="Leer lassen, um das Passwort unverändert zu lassen"
                />
                <ReferenceInput source="company" reference="companies" sort={{ field: 'name', order: 'ASC' }}>
                    <SelectInput optionText="displayName" validate={required()} fullWidth />
                </ReferenceInput>
            </FormTab>
            <FormTab label="resources.users.tabs.contact">
                <TextInput source="phone" fullWidth />
                <TextInput source="mobile" fullWidth />
                <TextInput source="street" fullWidth />
                <TextInput source="zip" />
                <TextInput source="city" fullWidth />
                <TextInput source="country" fullWidth />
            </FormTab>
            <FormTab label="resources.users.tabs.work">
                <CompanyFilteredWorkTab />
            </FormTab>
            <FormTab label="resources.users.tabs.personal">
                <DateInput source="dateOfBirth" label="Geburtsdatum" />
                <SelectInput source="language" label="Sprache" choices={[
                    { id: 'de', name: 'Deutsch' },
                    { id: 'en', name: 'English' },
                    { id: 'es', name: 'Español' },
                    { id: 'fr', name: 'Français' },
                    { id: 'it', name: 'Italiano' },
                ]} />
            </FormTab>
            <FormTab label="resources.users.tabs.roles">
                <ReferenceArrayInput source="userRoles" reference="roles" sort={{ field: 'name', order: 'ASC' }}>
                    <SelectArrayInput
                        optionText={roleOptionText}
                        label="Rollen"
                        fullWidth
                        sx={{ '& .MuiSelect-select': { minHeight: '1.4375em', paddingTop: '16.5px', paddingBottom: '16.5px' } }}
                    />
                </ReferenceArrayInput>
            </FormTab>
        </TabbedForm>
    </Edit>
    );
};

// Filtered department/group inputs — uses logged-in user's companyId from JWT
const CompanyFilteredWorkTab = () => {
    let companyId: number | undefined;
    try {
        const token = localStorage.getItem('token');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            companyId = payload.companyId ?? payload.company_id ?? undefined;
        }
    } catch {}

    const filter = companyId ? { 'company.id': companyId } : {};

    return (
        <>
            <TextInput source="jobTitle" fullWidth />
            <ReferenceInput source="department" reference="departments" label="Abteilung" sort={{ field: 'name', order: 'ASC' }} filter={filter}>
                <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
            <ReferenceArrayInput source="userGroups" reference="user_groups" sort={{ field: 'name', order: 'ASC' }} filter={filter}>
                <SelectArrayInput optionText="name" label="Benutzergruppen" fullWidth sx={{ '& .MuiSelect-select': { minHeight: '1.4375em', paddingTop: '16.5px', paddingBottom: '16.5px' } }} />
            </ReferenceArrayInput>
        </>
    );
};

const UserCreateToolbar = () => {
    const notify = useNotify();
    // react-hook-form ist react-admins interne Form-Engine. Ohne reset()
    // bleiben die gerade gespeicherten Werte im Form-State stehen — der
    // User sieht trotz "Speichern & Neu" den vorherigen Datensatz statt
    // eines leeren Formulars (Issue #292).
    const { reset } = useFormContext();
    return (
        <Toolbar>
            <SaveButton label="Speichern" />
            <SaveButton
                label="Speichern & Neu"
                mutationOptions={{
                    onSuccess: () => {
                        notify('Benutzer erfolgreich angelegt', { type: 'success' });
                        reset();
                    },
                }}
                type="button"
                variant="outlined"
                sx={{ ml: 2 }}
            />
        </Toolbar>
    );
};

export const UserCreate = () => {
    const notify = useNotify();
    return (
    <Create actions={<TopToolbar><ListButton /></TopToolbar>} mutationOptions={{ onSuccess: () => { notify('Benutzer erfolgreich angelegt', { type: 'success' }); } }}>
        {/* Issue #292 Folge: Browser-Autofill hat das leere Anlegen-Formular
            mit Login-Daten des Admins vorausgefuellt (Hofmann-Symptom).
            autoComplete="off" auf allen Textfeldern + "new-password" auf
            dem Passwort-Feld sind die Standard-Hints, die Chrome/Firefox
            respektieren, damit die Saved-Passwords-Overlay nicht greift. */}
        <TabbedForm toolbar={<UserCreateToolbar />}>
            <FormTab label="resources.users.tabs.general">
                <TextInput source="email" validate={required()} type="email" fullWidth autoComplete="off" />
                <TextInput source="password" type="password" validate={[required(), minLength(10)]} fullWidth autoComplete="new-password" />
                <TextInput source="firstName" fullWidth autoComplete="off" />
                <TextInput source="lastName" fullWidth autoComplete="off" />
                <ReferenceInput source="company" reference="companies" sort={{ field: 'name', order: 'ASC' }}>
                    <SelectInput optionText="displayName" validate={required()} fullWidth />
                </ReferenceInput>
            </FormTab>
            <FormTab label="resources.users.tabs.contact">
                <TextInput source="phone" fullWidth autoComplete="off" />
                <TextInput source="mobile" fullWidth autoComplete="off" />
                <TextInput source="street" fullWidth autoComplete="off" />
                <TextInput source="zip" autoComplete="off" />
                <TextInput source="city" fullWidth autoComplete="off" />
                <TextInput source="country" fullWidth autoComplete="off" />
            </FormTab>
            <FormTab label="resources.users.tabs.work">
                <CompanyFilteredWorkTab />
            </FormTab>
            <FormTab label="resources.users.tabs.personal">
                <DateInput source="dateOfBirth" label="Geburtsdatum" />
                <SelectInput source="language" label="Sprache" choices={[
                    { id: 'de', name: 'Deutsch' },
                    { id: 'en', name: 'English' },
                    { id: 'es', name: 'Español' },
                    { id: 'fr', name: 'Français' },
                    { id: 'it', name: 'Italiano' },
                ]} />
            </FormTab>
            <FormTab label="resources.users.tabs.roles">
                <ReferenceArrayInput source="userRoles" reference="roles" sort={{ field: 'name', order: 'ASC' }}>
                    <SelectArrayInput
                        optionText={roleOptionText}
                        label="Rollen"
                        fullWidth
                        sx={{ '& .MuiSelect-select': { minHeight: '1.4375em', paddingTop: '16.5px', paddingBottom: '16.5px' } }}
                    />
                </ReferenceArrayInput>
            </FormTab>
        </TabbedForm>
    </Create>
    );
};
